// src/store/reducers/blogPostReducer.js
import { BLOG_POST_ACTIONS } from '../actions/actionTypes';

const initialState = {
  blogPosts: [],
  selectedBlogPost: null,
  loading: false,
  error: null,
  success: null,
  meta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filters: {
    page: 1,
    limit: 20,
    search: '',
    status: '',
    categoryId: '',
    authorId: '',
    isFeatured: undefined,
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  comments: [],
  commentsMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  categories: [],
  categoriesMeta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  tags: [],
  tagsMeta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  relatedPosts: [],
  stats: null,
  uploadProgress: 0,
};

const blogPostReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch blog posts
    case BLOG_POST_ACTIONS.FETCH_BLOG_POSTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_POSTS_SUCCESS:
      return {
        ...state,
        loading: false,
        blogPosts: action.payload.data || [],
        meta: action.payload.meta || initialState.meta,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_POSTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        blogPosts: [],
      };

    // Fetch single blog post
    case BLOG_POST_ACTIONS.FETCH_BLOG_POST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedBlogPost: action.payload,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_POST_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        selectedBlogPost: null,
      };

    // Create blog post
    case BLOG_POST_ACTIONS.CREATE_BLOG_POST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case BLOG_POST_ACTIONS.CREATE_BLOG_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        blogPosts: [action.payload, ...state.blogPosts],
        success: 'Article créé avec succès',
        error: null,
      };

    case BLOG_POST_ACTIONS.CREATE_BLOG_POST_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update blog post
    case BLOG_POST_ACTIONS.UPDATE_BLOG_POST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case BLOG_POST_ACTIONS.UPDATE_BLOG_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        blogPosts: state.blogPosts.map(post =>
          post.id === action.payload.id ? action.payload : post
        ),
        selectedBlogPost: action.payload,
        success: 'Article mis à jour avec succès',
        error: null,
      };

    case BLOG_POST_ACTIONS.UPDATE_BLOG_POST_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Delete blog post
    case BLOG_POST_ACTIONS.DELETE_BLOG_POST_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case BLOG_POST_ACTIONS.DELETE_BLOG_POST_SUCCESS:
      return {
        ...state,
        loading: false,
        blogPosts: state.blogPosts.filter(post => post.id !== action.payload.id),
        success: action.payload.message || 'Article supprimé avec succès',
        error: null,
      };

    case BLOG_POST_ACTIONS.DELETE_BLOG_POST_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update blog post status
    case BLOG_POST_ACTIONS.UPDATE_BLOG_POST_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.UPDATE_BLOG_POST_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        blogPosts: state.blogPosts.map(post =>
          post.id === action.payload.id 
            ? { ...post, status: action.payload.status }
            : post
        ),
        selectedBlogPost: state.selectedBlogPost?.id === action.payload.id
          ? { ...state.selectedBlogPost, status: action.payload.status }
          : state.selectedBlogPost,
        success: `Statut mis à jour: ${action.payload.status}`,
        error: null,
      };

    case BLOG_POST_ACTIONS.UPDATE_BLOG_POST_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch blog comments
    case BLOG_POST_ACTIONS.FETCH_BLOG_COMMENTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_COMMENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        comments: action.payload.data || [],
        commentsMeta: action.payload.meta || initialState.commentsMeta,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_COMMENTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        comments: [],
      };

    // Update comment status
    case BLOG_POST_ACTIONS.UPDATE_COMMENT_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.UPDATE_COMMENT_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        comments: state.comments.map(comment =>
          comment.id === action.payload.commentId
            ? { ...comment, status: action.payload.status }
            : comment
        ),
        success: 'Statut du commentaire mis à jour',
        error: null,
      };

    case BLOG_POST_ACTIONS.UPDATE_COMMENT_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Delete comment
    case BLOG_POST_ACTIONS.DELETE_COMMENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.DELETE_COMMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        comments: state.comments.filter(comment => comment.id !== action.payload.commentId),
        success: action.payload.message || 'Commentaire supprimé',
        error: null,
      };

    case BLOG_POST_ACTIONS.DELETE_COMMENT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch blog categories
    case BLOG_POST_ACTIONS.FETCH_BLOG_CATEGORIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_CATEGORIES_SUCCESS:
      return {
        ...state,
        loading: false,
        categories: action.payload.data || [],
        categoriesMeta: action.payload.meta || initialState.categoriesMeta,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_CATEGORIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        categories: [],
      };

    // Fetch blog tags
    case BLOG_POST_ACTIONS.FETCH_BLOG_TAGS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_TAGS_SUCCESS:
      return {
        ...state,
        loading: false,
        tags: action.payload.data || [],
        tagsMeta: action.payload.meta || initialState.tagsMeta,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_TAGS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        tags: [],
      };

    // Upload featured image
    case BLOG_POST_ACTIONS.UPLOAD_FEATURED_IMAGE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        uploadProgress: 0,
      };

    case BLOG_POST_ACTIONS.UPLOAD_FEATURED_IMAGE_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedBlogPost: {
          ...state.selectedBlogPost,
          featuredImage: action.payload,
          featuredImageId: action.payload.id,
        },
        uploadProgress: 100,
        success: 'Image téléchargée avec succès',
        error: null,
      };

    case BLOG_POST_ACTIONS.UPLOAD_FEATURED_IMAGE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        uploadProgress: 0,
      };

    // Fetch blog stats
    case BLOG_POST_ACTIONS.FETCH_BLOG_STATS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_STATS_SUCCESS:
      return {
        ...state,
        loading: false,
        stats: action.payload,
        error: null,
      };

    case BLOG_POST_ACTIONS.FETCH_BLOG_STATS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        stats: null,
      };

    // Bulk delete
    case BLOG_POST_ACTIONS.BULK_DELETE_BLOG_POSTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case BLOG_POST_ACTIONS.BULK_DELETE_BLOG_POSTS_SUCCESS:
      return {
        ...state,
        loading: false,
        blogPosts: state.blogPosts.filter(post => !action.payload.ids.includes(post.id)),
        success: action.payload.message || 'Articles supprimés avec succès',
        error: null,
      };

    case BLOG_POST_ACTIONS.BULK_DELETE_BLOG_POSTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Set filters
    case BLOG_POST_ACTIONS.SET_BLOG_POST_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    // Clear states
    case BLOG_POST_ACTIONS.CLEAR_BLOG_POST_ERROR:
      return {
        ...state,
        error: null,
      };

    case BLOG_POST_ACTIONS.CLEAR_BLOG_POST_SUCCESS:
      return {
        ...state,
        success: null,
      };

    case BLOG_POST_ACTIONS.CLEAR_SELECTED_BLOG_POST:
      return {
        ...state,
        selectedBlogPost: null,
        comments: [],
        relatedPosts: [],
      };

    default:
      return state;
  }
};

export default blogPostReducer;